import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { invalidateEntity } from '../cache-invalidation';
import { initializeKeys, dataSyncKeys } from '../query-keys';
import { personioSetupRowsQueryOptions } from '../queries/useDataSync';
import { toastApiError } from './toast-api-error';
import {
  applyDataSyncContract,
  linkPersonioContract,
  listDataSyncExcelRowsContract,
  readPersonioForSetupContract,
  retryDataSyncContract,
  startExcelDataSyncContract,
  startPersonioDataSyncContract,
  testPersonioConnectionContract,
  unlinkPersonioContract,
  type ApplyDataSyncRequest,
  type ListDataSyncExcelRowsResponse,
  type DataSyncOkResponse,
  type DataSyncStatus,
  type DataSyncRun,
  type PersonioCredentials,
  type StartExcelDataSyncRequest
} from '@bliks-io/contracts';

/** Caches the run the response returned; it also changes the status's open run. */
function storeRun(queryClient: QueryClient, run: DataSyncRun) {
  queryClient.setQueryData(dataSyncKeys.syncRun(run.id), run);
  return queryClient.invalidateQueries({ queryKey: dataSyncKeys.status() });
}

/** Start a sync run from the rows of an Excel file parsed in the browser. */
export function useStartExcelDataSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: StartExcelDataSyncRequest) => {
      const { data } = await apiClient.post<DataSyncRun>(startExcelDataSyncContract.path, request);
      return data;
    },
    onSuccess: (run) => storeRun(queryClient, run),
    // A refused start (another run reads or applies) means the status on screen is stale.
    onError: () => queryClient.invalidateQueries({ queryKey: dataSyncKeys.status() })
  });
}

/** Read Personio and start a sync run; a failed read returns the run as failed. */
export function useStartPersonioDataSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<DataSyncRun>(
        startPersonioDataSyncContract.path,
        undefined,
        // The start reads all of Personio with pacing and can outlast the 30 s default.
        { timeout: 0 }
      );
      return data;
    },
    onSuccess: (run) => storeRun(queryClient, run),
    onError: () => queryClient.invalidateQueries({ queryKey: dataSyncKeys.status() })
  });
}

/**
 * Writes differences of a run. A write creates, changes and deletes people,
 * departments and teams, so every list holding them goes stale.
 */
function useSyncRunWrite<V extends { runId: string }>(
  write: (variables: V) => Promise<DataSyncRun>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: write,
    onSuccess: (run) => {
      void storeRun(queryClient, run);
      void invalidateEntity(queryClient, ['orgMember', 'department', 'orgFunction', 'system']);
      void queryClient.invalidateQueries({ queryKey: initializeKeys.all });
    },
    // A refused or timed-out write may have moved the run on (stale, superseded, still applying).
    onError: (_, { runId }) => {
      void queryClient.invalidateQueries({ queryKey: dataSyncKeys.syncRun(runId) });
      void queryClient.invalidateQueries({ queryKey: dataSyncKeys.status() });
    }
  });
}

async function postRun(path: string, runId: string, body?: ApplyDataSyncRequest) {
  const { data } = await apiClient.post<DataSyncRun>(
    path.replace(':id', runId),
    body,
    // The write runs every difference in sequence and can outlast the 30 s default.
    { timeout: 0 }
  );
  return data;
}

/** Apply the selected differences of a run in review. */
export const useApplyDataSync = () =>
  useSyncRunWrite(({ runId, ...request }: ApplyDataSyncRequest & { runId: string }) =>
    postRun(applyDataSyncContract.path, runId, request)
  );

/** Write the failed differences of a done run again. */
export const useRetryDataSync = () =>
  useSyncRunWrite(({ runId }: { runId: string }) => postRun(retryDataSyncContract.path, runId));

/** Unlink Personio; deletes nothing. */
export function useUnlinkPersonio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.delete<DataSyncStatus>(unlinkPersonioContract.path);
      return data;
    },
    onSuccess: (status) => {
      queryClient.setQueryData(dataSyncKeys.status(), status);
      // The unlink drops the setup snapshot; a disabled query would still hand out its cached rows.
      queryClient.removeQueries({ queryKey: dataSyncKeys.setupRows() });
    }
  });
}

/** Link Personio as the data source with API credentials. */
export function useLinkPersonio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: dataSyncKeys.linkPersonio(),
    // Drops the credentials from the mutation cache once the form unmounts.
    gcTime: 0,
    mutationFn: async (credentials: PersonioCredentials) => {
      const { data } = await apiClient.post<DataSyncStatus>(linkPersonioContract.path, credentials);
      return data;
    },
    onSuccess: (status) => {
      queryClient.setQueryData(dataSyncKeys.status(), status);
    }
  });
}

/** Test Personio API credentials without storing them. */
export function useTestPersonioConnection() {
  return useMutation({
    // Drops the credentials from the mutation cache once the form unmounts.
    gcTime: 0,
    mutationFn: async (credentials: PersonioCredentials) => {
      const { data } = await apiClient.post<DataSyncOkResponse>(
        testPersonioConnectionContract.path,
        credentials
      );
      return data;
    }
  });
}

/**
 * Read the linked Personio, store the snapshot the setup rows seed from, and
 * return the fresh setup rows. `onSuccess` runs on the mutation itself, so it
 * still runs when the step that started the read was left before the read
 * finished.
 */
export function useReadPersonioForSetup({ onSuccess }: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: dataSyncKeys.readPersonioForSetup(),
    mutationFn: async () => {
      await apiClient.post<DataSyncOkResponse>(
        readPersonioForSetupContract.path,
        undefined,
        // The read pulls all of Personio with pacing and can outlast the 30 s default.
        { timeout: 0 }
      );
      // The cached rows predate this read: an invalidated query is fetched despite
      // staleTime Infinity, and stays invalidated if that fetch fails.
      await queryClient.invalidateQueries({
        queryKey: dataSyncKeys.setupRows(),
        refetchType: 'none'
      });
      return queryClient.fetchQuery(personioSetupRowsQueryOptions());
    },
    onSuccess: () => onSuccess?.(),
    onError: (error) => toastApiError(error, 'errors.unknown')
  });
}

/** Fetch the rows of the Excel download on demand, with the Bliks id column. */
export function useDownloadDataSyncExcelRows() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<ListDataSyncExcelRowsResponse>(
        listDataSyncExcelRowsContract.path
      );
      return data;
    }
  });
}
